if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.querySelector('[data-o2-price-at-add]')?.removeAttribute('disabled');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[data-o2-main-product-submit], [type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.disabled ||
          this.submitButton.hasAttribute('data-product-watchdog-o2') ||
          this.submitButton.getAttribute('aria-disabled') === 'true'
        )
          return;

        const focusError = evt.submitter?.matches('[data-o2-floating-submit]') === true;
        this.handleErrorMessage();
        const activeSubmitButton = evt.submitter || this.submitButton;
        const campaignDiscountCode = this.getCampaignDiscountCode();

        this.submitButton.setAttribute('aria-disabled', true);
        activeSubmitButton.setAttribute('aria-disabled', true);
        activeSubmitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const selectedCrossSellItems = this.getSelectedCrossSellItems(formData.get('id'));
        const discountCodesToApply = this.getUniqueDiscountCodes([
          campaignDiscountCode,
          ...selectedCrossSellItems.map((item) => item.discountCode),
        ]);

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (selectedCrossSellItems.length) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(this.buildBundledCartAddPayload(formData, selectedCrossSellItems));
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description, focusError);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const cartUpdatePromise = this.error
              ? Promise.resolve()
              : publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });

            this.error = false;
            if (discountCodesToApply.length) {
              const updateDiscounts = async (sections = []) => {
                try {
                  const discountResult = await this.appendDiscountCodes(discountCodesToApply, { sections });
                  const campaignFailed =
                    campaignDiscountCode &&
                    discountResult.unappliedCodes.some(
                      (code) => code.toLowerCase() === campaignDiscountCode.toLowerCase()
                    );

                  if (campaignFailed) {
                    const errorMessage = this.getCampaignDiscountErrorMessage();
                    this.handleErrorMessage(errorMessage, focusError);
                    window.O2CampaignDiscount?.showError(errorMessage);
                  }

                  return { campaignFailed, cartState: discountResult.cartState };
                } catch (error) {
                  console.error(error);
                  const campaignFailed = Boolean(campaignDiscountCode);
                  if (campaignFailed) {
                    const errorMessage = this.getCampaignDiscountErrorMessage();
                    this.handleErrorMessage(errorMessage, focusError);
                    window.O2CampaignDiscount?.showError(errorMessage);
                  }
                  return { campaignFailed, cartState: null };
                }
              };

              if (!this.cart) {
                return cartUpdatePromise.finally(async () => {
                  const discountResult = await updateDiscounts();
                  if (discountResult.campaignFailed) {
                    window.O2CampaignDiscount?.persistError(this.getCampaignDiscountErrorMessage());
                  }
                  window.location = window.routes.cart_url;
                });
              }

              const sections = this.cart.getSectionsToRender().map((section) => section.id);
              const discountUpdatePromise = updateDiscounts(sections);

              return cartUpdatePromise.finally(() => {
                return discountUpdatePromise.then(({ cartState }) => {
                  CartPerformance.measure('add:paint-updated-sections', () => {
                    this.cart.renderContents(
                      cartState?.sections ? { ...response, sections: cartState.sections } : response
                    );
                  });
                });
              });
            }

            if (!this.cart) {
              cartUpdatePromise.finally(() => {
                window.location = window.routes.cart_url;
              });
              return;
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            activeSubmitButton.removeAttribute('aria-busy');
            if (!this.error) activeSubmitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false, focusError = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          if (focusError) {
            this.errorMessageWrapper.scrollIntoView({ block: 'center' });
            this.errorMessageWrapper.focus({ preventScroll: true });
          }
        }
      }

      getSelectedCrossSellItems(mainVariantId) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return [];

        const mainVariantIdString = String(mainVariantId || '').trim();
        const seenVariantIds = new Set();
        return Array.from(productInfo.querySelectorAll('[data-cross-sell-item]:checked:not(:disabled)'))
          .map((input) => ({
            variantId: String(input.dataset.variantId || '').trim(),
            priceAtAdd: String(input.dataset.priceAtAdd || '').trim(),
            discountCode: this.normalizeDiscountCode(input.dataset.crossSellDiscountCode),
          }))
          .filter((item) => {
            if (!item.variantId || item.variantId === mainVariantIdString || seenVariantIds.has(item.variantId)) {
              return false;
            }
            seenVariantIds.add(item.variantId);
            return true;
          });
      }

      normalizeDiscountCode(code) {
        return String(code || '').trim();
      }

      getCampaignDiscountCode() {
        const productSection =
          this.closest('product-info') ||
          document.getElementById(`MainProduct-${this.dataset.sectionId}`) ||
          this.closest('section');
        return this.normalizeDiscountCode(
          productSection?.querySelector('[data-o2-campaign-discount-code]')?.dataset.o2CampaignDiscountCode
        );
      }

      getCampaignDiscountErrorMessage() {
        return (
          this.dataset.campaignDiscountError ||
          'The product was added to the cart, but the discount code could not be applied.'
        );
      }

      getUniqueDiscountCodes(codes) {
        const seen = new Set();
        return codes
          .map((code) => this.normalizeDiscountCode(code))
          .filter((code) => {
            const key = code.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
      }

      async appendDiscountCodes(codes, { sections = [] } = {}) {
        if (!window.O2CampaignDiscount) {
          throw new Error('Campaign discount helper is unavailable.');
        }
        return window.O2CampaignDiscount.apply(codes, { sections });
      }

      buildMainLineItem(formData) {
        const variantId = Number(formData.get('id'));
        const quantityRaw = Number(formData.get('quantity'));
        const quantity = Number.isFinite(quantityRaw) && quantityRaw > 0 ? quantityRaw : 1;

        const mainItem = {
          id: variantId,
          quantity,
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) {
          mainItem.selling_plan = String(sellingPlan);
        }

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match) continue;
          properties[match[1]] = String(value);
        }

        if (Object.keys(properties).length) {
          mainItem.properties = properties;
        }

        return mainItem;
      }

      buildBundledCartAddPayload(formData, crossSellItems) {
        const payload = {
          items: [
            this.buildMainLineItem(formData),
            ...crossSellItems.map((item) => o2BuildCrossSellLineItem(item.variantId, item.priceAtAdd)),
          ],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        return payload;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
